import { ViewModel } from "mvvm/ViewModel";
import { PageViewModel } from "./PageViewModel";
import { JobFeedback, JobInfo } from "../../../../services/NLIJobService";
import { DashJobOutput } from "../../../../services/DashJobService";
import { TagViewModel } from "./TagViewModel";
import { notification } from "antd";

export type JobOutputState = Readonly<
    { status: "not-loaded"|"loading" } |
    { status: "loaded", output: DashJobOutput } |
    { status: "load-failed", error: string }>;

export class JobViewModel extends ViewModel {

    constructor(public readonly page: PageViewModel, info: JobInfo) {
        super();
        this.jobID = info.jobID;
        this._startTime = info.startTime;
        this._endTime = info.endTime;
        this._alias = info.alias;
        this._question = info.question;
        this._status = info.status;
        this._statusMessage = info.statusMessage;
        this._outputState = { status: "not-loaded" };
        this._tags = info.tags;
        this.tagViewModel = new TagViewModel(this);
        this._replyTarget = info.replyTarget;
        this._scheduleTime = info.scheduleTime;
        this._scheduleType = info.scheduleType;
        this.updateExecutionTime();
    }

    readonly jobID: string;
    private _startTime: number;
    private _endTime?: number;
    private _question: string;
    readonly tagViewModel: TagViewModel;
    private _replyTarget?: string;
    private _alias: string;
    private _status: string;
    private _statusMessage?: string;
    private _outputState: JobOutputState;
    private _isSelected = false;
    private _executionTime: string = "0s";
    private _tags: string[];
    private _scheduleTime?: string;
    private _scheduleType?: string;

    get scheduleTime() {
        return this._scheduleTime;
    }

    get scheduleType() {
        return this._scheduleType;
    }

    get status() {
        return this._status;
    }

    set status(value: string) {
        if (value !== this._status) {
            this._status = value;
            if (value === "RUNNING") {
                this._outputState = { status: "not-loaded" };
            }
            this.updateViews();
        }
    }

    get statusMessage() {
        return this._statusMessage;
    }

    private set statusMessage(value: string|undefined) {
        if (value !== this._statusMessage) {
            this._statusMessage = value;
            this.updateViews();
        }
    }

    get tags() {
        return this._tags;
    }

    set tags(value: string[]) {
        this._tags = value;
        this.updateViews();
    }

    get outputState() {
        return this._outputState;
    }

    private set outputState(value: JobOutputState) {
        if (value !== this._outputState) {
            this._outputState = value;
            this.updateViews();
        }
    }

    get isSelected() {
        return this._isSelected;
    }

    set isSelected(value: boolean) {
        if (value !== this._isSelected) {
            this._isSelected = value;
            if (value) {
                if (this.page.selectedJob !== this) {
                    this.page.selectedJob = this;
                }
                if (this.status !== "RUNNING") {
                    this.loadOutput();
                }
            } else {
                if (this.page.selectedJob === this) {
                    this.page.selectedJob = undefined;
                }
            }
        }
    }

    get executionTime() {
        return this._executionTime;
    }

    updateExecutionTime(serverTime?: number) {
        if (this.endTime && this.status !== "RUNNING") {
            // Completed job - use actual execution time
            const executionTimeMs = this.endTime - this.startTime;
            const executionTimeSec = Math.round(executionTimeMs / 1000);
            this._executionTime = executionTimeSec < 60 ? `${executionTimeSec}s` :
                executionTimeSec < 3600 ? `${Math.floor(executionTimeSec / 60)}m` :
                `${Math.floor(executionTimeSec / 3600)}h`;
        } else if (this.status === "RUNNING" && serverTime) {
            // Running job - show elapsed time so far
            const elapsedTimeMs = serverTime - this.startTime;
            const elapsedTimeSec = Math.round(elapsedTimeMs / 1000);
            this._executionTime = elapsedTimeSec < 60 ? `${elapsedTimeSec}s` :
                elapsedTimeSec < 3600 ? `${Math.floor(elapsedTimeSec / 60)}m` :
                `${Math.floor(elapsedTimeSec / 3600)}h`;
        } else if (this.status === "RUNNING") {
            this._executionTime = "0s";
        } else {
            this._executionTime = "0s";
        }
        this.updateViews();
    }

    updateStatus(status: string, alias: string, statusMessage?: string, endTime?: number) {
        if (status !== this.status) {
            this.page.invalidateJobList();
        }
        this.status = status;
        this.statusMessage = statusMessage;
        this.alias = alias;
        if (endTime !== undefined) {
            this._endTime = endTime;
        }
        this.updateExecutionTime();

        if (this.isSelected && status !== "RUNNING") {
            this.loadOutput();
        }
    }

    get question() {
        return this._question;
    }

    get startTime() {
        return this._startTime;
    }

    get endTime() {
        return this._endTime;
    }

    get replyTarget() {
        return this._replyTarget;
    }

    async loadOutput() {
        if (this.outputState.status === "not-loaded") {
            try {
                this.outputState = { status: "loading" };
                const output = await this.page.jobService.loadJobOutput(this.jobID);
                this.outputState = { status: "loaded", output };
            } catch (error) {
                this.outputState = { status: "load-failed", error: this.formatError(error) };
            }
        }
    }

    get alias() {
        return this._alias;
    }

    set alias(value: string) {
        if (value !== this._alias) {
            this._alias = value;
            this.updateViews();
        }
    }

    get canDownloadOutput() {
        if (this.outputState.status !== "loaded") return false;
        const output = this.outputState.output;
        // Check if any Data outputs exist
        for (let i = 1; i <= 24; i++) {
            const dataKey = `Data${i}` as keyof DashJobOutput;
            if (output[dataKey]) return true;
        }
        return !!output.Error || !!output.Formatting;
    }

    downloadOutput() {
        if (this.outputState.status === "loaded") {
            const output = this.outputState.output;
            // Download first available Data output, or Error, or Formatting
            for (let i = 1; i <= 24; i++) {
                const dataKey = `Data${i}` as keyof DashJobOutput;
                const data = output[dataKey];
                if (data) {
                    this.page.jobService.download(this.jobID, data.name, data.extension);
                    return;
                }
            }
            if (output.Error) {
                this.page.jobService.download(this.jobID, output.Error.name, output.Error.extension);
            } else if (output.Formatting) {
                this.page.jobService.download(this.jobID, output.Formatting.name, output.Formatting.extension);
            }
        }
    }

    async deleteJob() {
        this.page.isSubmitting = true;
        try {
            await this.page.jobService.deleteJob(this.jobID);
            this.page.removeJob(this);
            notification.success({
                message: this.replyTarget ? 'Request deleted' : 'Stream deleted',
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to delete',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        } finally {
            this.page.isSubmitting = false;
        }
    }

    private areTagsEqual(a: string[], b: string[]) {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    }

    private isFeedbackEqual(a?: JobFeedback, b?: JobFeedback) {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.rating === b.rating && a.comment === b.comment;
    }

    updateFromJobInfo(info: JobInfo) {
        let fieldsChanged = false;

        if (info.question !== this._question) {
            this._question = info.question;
            fieldsChanged = true;
        }

        if (info.startTime !== this._startTime) {
            this._startTime = info.startTime;
            fieldsChanged = true;
        }

        if (info.endTime !== this._endTime) {
            this._endTime = info.endTime;
            fieldsChanged = true;
        }

        if (info.replyTarget !== this._replyTarget) {
            this._replyTarget = info.replyTarget;
            fieldsChanged = true;
        }

        if (!this.areTagsEqual(this._tags, info.tags)) {
            this.tags = info.tags;
        }

        if (info.scheduleTime !== this._scheduleTime) {
            this._scheduleTime = info.scheduleTime;
            fieldsChanged = true;
        }

        if (info.scheduleType !== this._scheduleType) {
            this._scheduleType = info.scheduleType;
            fieldsChanged = true;
        }

        this.updateStatus(info.status, info.alias, info.statusMessage, info.endTime);

        if (fieldsChanged) {
            this.updateViews();
        }
    }
}